import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import MasterLayout from '../../masterLayout/MasterLayout';
import Breadcrumb from '../../components/Breadcrumb';
import KkpEntrepriseAutocomplete from '../../components/kkp/KkpEntrepriseAutocomplete';
import PlanDeComClientInfoModal from '../../components/kkp/PlanDeComClientInfoModal';
import PlanDeComDossierSwitcher from '../../components/kkp/PlanDeComDossierSwitcher';
import PlanDeComPreviewModal from '../../components/kkp/PlanDeComPreviewModal';
import PlanDeComPdfPreview from '../../components/kkp/PlanDeComPdfPreview';
import { AppFilterBar, AppFilterCommercialSelect, AppFilterField } from '../../components/filters';
import KkpPlanningPageActions from '../../components/kkp/KkpPlanningPageActions';
import KkpPlanningEmptyState from '../../components/kkp/KkpPlanningEmptyState';
import useKlubChiffrageSession from '../../hooks/useKlubChiffrageSession';
import useChiffrageDossierActions from '../../hooks/useChiffrageDossierActions';
import { useSetAssistantPageClient } from '../../context/AssistantPageClientContext';
import klubChiffrageService from '../../services/klubChiffrageService';
import { notify } from '../../components/common/AppToast';
import {
  clientFormToSavePayload,
  dolibarrClientToDossierPatch,
  needsBdcClientInfoModal,
} from '../../utils/planDeComClientInfo';
import kkpService from '../../services/kkpService';
import { formatPlanDeComMoney } from '../../utils/planDeComCalendrierUi';
import { matchCommercialOption } from '../../utils/matchCommercialOption';
import authService from '../../services/authService';
import useAppConfirm from '../../hooks/useAppConfirm';
import { mergeDossierAfterSave } from '../../utils/mergeDossierAfterSave';
import { AppAlert, AppButton, AppInput, AppSelect } from '../../components/ui';
import { ROUTES } from '../../config/routes';
import '../../styles/kkp-planning-page.scss';

const tarifForEmployees = (offer, employees) => {
  const count = Number(employees) || 0;
  if (!offer) return 0;
  if (count <= 10) return Number(offer.tarif_1_10) || 0;
  if (count <= 20) return Number(offer.tarif_11_20) || 0;
  if (count <= 48) return Number(offer.tarif_21_48) || 0;
  return Number(offer.tarif_49_plus) || 0;
};

const productRefForEmployees = (offer, employees) => {
  const count = Number(employees) || 0;
  if (!offer) return null;
  if (count <= 10) return offer.product_ref_1_10 || null;
  if (count <= 20) return offer.product_ref_11_20 || null;
  if (count <= 48) return offer.product_ref_21_48 || null;
  return offer.product_ref_49_plus || null;
};

const KlubChiffragePage = () => {
  const { ask, modal } = useAppConfirm();
  const {
    data: sessionData,
    dossiers,
    loading,
    error,
    saving,
    dossierId,
    save,
    reset,
    startNew,
    openDossier,
    duplicateDossier,
    deleteDossier,
    renameDossier,
    loadDossiers,
  } = useKlubChiffrageSession();

  const [data, setData] = useState(null);
  const [meta, setMeta] = useState(null);
  const [metaError, setMetaError] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [generatingBdc, setGeneratingBdc] = useState(false);
  const [pushingDolibarr, setPushingDolibarr] = useState(false);
  const [clientInfoModalOpen, setClientInfoModalOpen] = useState(false);
  const [clientInfoPurpose, setClientInfoPurpose] = useState('download');
  const [previewKind, setPreviewKind] = useState(null);
  const [previewPdfUrl, setPreviewPdfUrl] = useState(null);
  const [previewPdfLoading, setPreviewPdfLoading] = useState(false);
  const [previewPdfError, setPreviewPdfError] = useState(null);
  const [previewBdcOptions, setPreviewBdcOptions] = useState(null);
  const [resolvingClient, setResolvingClient] = useState(false);
  const saveTimerRef = useRef(null);
  const dataInitializedRef = useRef(false);
  const lastDossierIdRef = useRef(null);
  const persistGenRef = useRef(0);

  const assistantClientLabel = data?.dossier?.client_label
    || data?.dossier?.entreprise_label
    || '';
  useSetAssistantPageClient(assistantClientLabel);

  const klubOffers = meta?.klub_offers ?? [];
  const selectedKlubOffer = useMemo(() => {
    const selectedName = String(data?.dossier?.type_abonnement_klub ?? '').trim().toLowerCase();
    if (!selectedName) return null;
    return klubOffers.find((offer) => String(offer.nom ?? '').trim().toLowerCase() === selectedName) ?? null;
  }, [data?.dossier?.type_abonnement_klub, klubOffers]);

  useEffect(() => {
    klubChiffrageService.getMeta().then(setMeta).catch((e) => setMetaError(e.message));
  }, []);

  useEffect(() => {
    if (!sessionData) return;
    const nextId = sessionData.dossier?.id ?? null;
    if (dataInitializedRef.current && nextId === lastDossierIdRef.current) return;
    lastDossierIdRef.current = nextId;
    setData(sessionData);
    dataInitializedRef.current = true;
  }, [sessionData]);

  const persist = useCallback(async (payload) => {
    if (!dossierId) return null;
    const gen = ++persistGenRef.current;
    const full = await save(payload);
    if (full && gen === persistGenRef.current) {
      setData((prev) => ({
        ...full,
        dossier: mergeDossierAfterSave(prev?.dossier, full.dossier, payload),
      }));
    }
    return full;
  }, [dossierId, save]);

  const scheduleSave = useCallback((nextPayload, delay = 120) => {
    if (!dossierId) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => persist(nextPayload), delay);
  }, [dossierId, persist]);

  const updateDossierField = (field, value) => {
    const patch = { [field]: value };
    setData((prev) => ({
      ...prev,
      dossier: { ...prev.dossier, ...patch },
    }));
    // Délai un peu plus long sur le titre / textes pour limiter les courses
    // entre frappe et réponse API.
    const delay = field === 'titre' || field === 'client_label' ? 400 : 120;
    scheduleSave(patch, delay);
  };

  const handleEntrepriseSelect = ({
    id,
    nom,
    client_label: clientLabel,
    commercial_id: commercialId,
    commercial_nom: commercialNom,
    commercial_email: commercialEmail,
    adresse,
    cp,
    ville,
    telephone,
    email,
  }) => {
    if (!data?.dossier) return;
    const companyName = String(nom ?? '').trim();
    const contactName = String(clientLabel ?? '').trim();
    const patch = {
      entreprise_label: companyName || null,
      dolibarr_thirdparty_id: id ?? null,
      client_label: contactName || null,
      client_adresse: adresse ?? null,
      client_cp: cp ?? null,
      client_ville: ville ?? null,
      client_telephone: telephone ?? null,
      client_email: email ?? null,
    };
    const matchedCommercial = matchCommercialOption(
      (meta?.commerciaux ?? []).map((c) => ({
        value: String(c.id),
        label: c.nom,
        email: c.email,
      })),
      {
        commercial_id: commercialId,
        commercial_nom: commercialNom,
        commercial_email: commercialEmail,
      },
    );
    if (matchedCommercial) {
      patch.commercial_id = Number(matchedCommercial.value);
    }
    setData((prev) => ({
      ...prev,
      dossier: { ...prev.dossier, ...patch },
    }));
    persist(patch);
  };

  const handleEntrepriseResolving = (resolving) => {
    setResolvingClient(resolving);
    if (!resolving) return;
    setData((prev) => (
      prev
        ? { ...prev, dossier: { ...prev.dossier, client_label: '' } }
        : prev
    ));
  };

  const flushPendingSave = useCallback(async (extraPayload = {}) => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    if (!dossierId || !data) return null;
    const dossier = data.dossier ?? {};
    return persist({
      titre: dossier.titre ?? null,
      entreprise_label: dossier.entreprise_label ?? null,
      client_label: dossier.client_label ?? null,
      dolibarr_thirdparty_id: dossier.dolibarr_thirdparty_id ?? null,
      client_adresse: dossier.client_adresse ?? null,
      client_cp: dossier.client_cp ?? null,
      client_ville: dossier.client_ville ?? null,
      client_telephone: dossier.client_telephone ?? null,
      client_email: dossier.client_email ?? null,
      commercial_id: dossier.commercial_id ?? null,
      type_abonnement_klub: dossier.type_abonnement_klub ?? null,
      nombre_salaries_klub: dossier.nombre_salaries_klub ?? null,
      ...extraPayload,
    });
  }, [dossierId, data, persist]);

  const revokePreviewPdfUrl = useCallback(() => {
    setPreviewPdfUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  }, []);

  const closePreview = useCallback(() => {
    setPreviewKind(null);
    setPreviewPdfError(null);
    setPreviewPdfLoading(false);
    setPreviewBdcOptions(null);
    revokePreviewPdfUrl();
  }, [revokePreviewPdfUrl]);

  const openPdfPreview = useCallback(async ({ fetchPdf, bdcOptions = null }) => {
    setPreviewKind('bdc');
    setPreviewPdfLoading(true);
    setPreviewPdfError(null);
    setPreviewBdcOptions(bdcOptions);
    revokePreviewPdfUrl();
    try {
      const { blob } = await fetchPdf();
      setPreviewPdfUrl(URL.createObjectURL(blob));
    } catch (err) {
      setPreviewPdfError(err?.message ?? 'Impossible de charger l’aperçu PDF');
      throw err;
    } finally {
      setPreviewPdfLoading(false);
    }
  }, [revokePreviewPdfUrl]);

  const openClientInfoForBdc = useCallback(async (purpose) => {
    if (!dossierId || generatingBdc || previewPdfLoading || pushingDolibarr) return;

    if (purpose === 'dolibarr') {
      if (!meta?.can_push_dolibarr) {
        notify.warning('API Dolibarr non configurée.');
        return;
      }
      if (!data?.dossier?.dolibarr_thirdparty_id) {
        notify.warning('Sélectionnez un client dans la liste Dolibarr (champ Entreprise).');
        return;
      }
      if (!data?.dossier?.commercial_id) {
        notify.warning('Sélectionnez un commercial avant d’envoyer vers Dolibarr.');
        return;
      }
      if (!data?.dossier?.type_abonnement_klub || !(Number(data?.dossier?.nombre_salaries_klub) > 0)) {
        notify.warning('Choisissez une offre et le nombre de salariés avant d’envoyer vers Dolibarr.');
        return;
      }
    }

    let dossier = data?.dossier ?? null;
    const thirdpartyId = Number(dossier?.dolibarr_thirdparty_id) || 0;

    if (needsBdcClientInfoModal(dossier) && thirdpartyId > 0) {
      try {
        const client = await kkpService.getDolibarrClient(thirdpartyId);
        const patch = dolibarrClientToDossierPatch(client, dossier);
        setData((prev) => (
          prev
            ? { ...prev, dossier: { ...prev.dossier, ...patch } }
            : prev
        ));
        await persist(patch);
      } catch {
        // Modal de saisie manuelle.
      }
    }

    setClientInfoPurpose(purpose);
    setClientInfoModalOpen(true);
  }, [
    dossierId,
    generatingBdc,
    previewPdfLoading,
    pushingDolibarr,
    meta?.can_push_dolibarr,
    data?.dossier,
    persist,
  ]);

  const runBdcFromClientModal = useCallback(async ({ client = null, bdcOptions = {} } = {}) => {
    if (!dossierId || generatingBdc || previewPdfLoading || pushingDolibarr) return;
    const isPreview = clientInfoPurpose === 'preview';
    const isDolibarr = clientInfoPurpose === 'dolibarr';

    if (isDolibarr) {
      setPushingDolibarr(true);
      try {
        await notify.run(async () => {
          const full = await flushPendingSave(client ? clientFormToSavePayload(client) : {});
          if (!full) return null;
          const res = await klubChiffrageService.pushDolibarr(dossierId, bdcOptions);
          setClientInfoModalOpen(false);
          if (res?.propal_url) {
            window.open(res.propal_url, '_blank', 'noopener,noreferrer');
          }
          return res;
        }, {
          loading: 'Création de la propal Dolibarr…',
          success: (res) => `Propal ${res.propal_ref || `#${res.propal_id}`} créée pour ${res.client_name}`,
        });
      } catch {
        // Toast déjà affiché
      } finally {
        setPushingDolibarr(false);
      }
      return;
    }

    if (isPreview) {
      setGeneratingBdc(true);
      try {
        await notify.run(async () => {
          await flushPendingSave(client ? clientFormToSavePayload(client) : {});
          await klubChiffrageService.prepareBdc(dossierId);
          setClientInfoModalOpen(false);
          await openPdfPreview({
            bdcOptions,
            fetchPdf: () => klubChiffrageService.fetchBdcPdf(dossierId, bdcOptions),
          });
        }, {
          loading: 'Préparation de l’aperçu…',
          success: 'Aperçu PDF prêt',
        });
      } catch {
        // Toast déjà affiché
      } finally {
        setGeneratingBdc(false);
      }
      return;
    }

    setGeneratingBdc(true);
    try {
      await notify.run(async () => {
        await flushPendingSave(client ? clientFormToSavePayload(client) : {});
        await klubChiffrageService.prepareBdc(dossierId);
        await klubChiffrageService.downloadBdcPdf(dossierId, bdcOptions);
        setClientInfoModalOpen(false);
      }, {
        loading: 'Préparation du devis…',
        success: 'Devis téléchargé',
      });
    } catch {
      // Toast déjà affiché
    } finally {
      setGeneratingBdc(false);
    }
  }, [
    dossierId,
    generatingBdc,
    previewPdfLoading,
    pushingDolibarr,
    clientInfoPurpose,
    flushPendingSave,
    openPdfPreview,
  ]);

  const handleGenerateBdc = () => openClientInfoForBdc('download');
  const handlePushDolibarr = () => openClientInfoForBdc('dolibarr');
  const openBdcPreview = () => openClientInfoForBdc('preview');

  const downloadPreviewPdf = async () => {
    if (!dossierId || generatingBdc) return;
    setGeneratingBdc(true);
    try {
      await notify.run(
        () => klubChiffrageService.downloadBdcPdf(dossierId, previewBdcOptions || {}),
        { loading: 'Téléchargement…', success: 'PDF téléchargé' },
      );
    } catch {
      // Toast déjà affiché
    } finally {
      setGeneratingBdc(false);
    }
  };

  const handleReset = async () => {
    if (!dossierId || resetting) return;
    const ok = await ask({
      title: 'Réinitialiser ce chiffrage ?',
      message: 'L’offre et le nombre de salariés seront remis à zéro.',
      confirmLabel: 'Réinitialiser',
      confirmVariant: 'danger',
    });
    if (!ok) return;
    setResetting(true);
    try {
      const full = await reset();
      if (full) setData(full);
      notify.success('Chiffrage réinitialisé.');
    } catch {
      notify.error('Impossible de réinitialiser.');
    } finally {
      setResetting(false);
    }
  };

  const {
    handleOpenDossier,
    handleNewDossier,
    handleDuplicateDossier,
    handleDeleteDossier,
    handleRenameDossier,
  } = useChiffrageDossierActions({
    session: {
      dossierId,
      saving,
      openDossier,
      startNew,
      duplicateDossier,
      deleteDossier,
      renameDossier,
    },
    ask,
    saveTimerRef,
    flushBeforeSwitch: async () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    },
    onSessionApplied: (full) => {
      setData(full);
    },
    labels: {
      deleteTitle: 'Supprimer ce chiffrage Klub ?',
    },
    skipSameDossier: false,
  });

  const commercialOptions = useMemo(
    () => (meta?.commerciaux ?? []).map((c) => ({ value: String(c.id), label: c.nom })),
    [meta],
  );
  const displayError = error || metaError;
  const toolsBusy = generatingBdc || pushingDolibarr || saving || previewPdfLoading;
  const recap = data?.recap;
  const dossierOptions = useMemo(() => {
    const list = [...(dossiers ?? [])];
    const current = data?.dossier;
    if (current?.id && !list.some((item) => item.id === current.id)) {
      list.unshift(current);
    }
    return list;
  }, [dossiers, data?.dossier]);
  const inclusions = [
    recap?.has_site_annuaire && 'Site annuaire',
    recap?.has_afterwork && 'Afterwork',
    recap?.has_ce && 'Offre CE',
  ].filter(Boolean);
  const selectedAboProductRef = productRefForEmployees(
    selectedKlubOffer,
    data?.dossier?.nombre_salaries_klub,
  );

  return (
    <MasterLayout>
      <Breadcrumb
        title="Chiffrage klub"
        subtitle="Calculez l’abonnement, générez le BDC et envoyez-le vers Dolibarr"
        actions={(
          <div className="d-flex gap-2 flex-wrap align-items-center">
            <AppButton
              as={Link}
              to={ROUTES.klub.dashboard}
              size="sm"
              variant="outline-secondary"
              icon="mdi:arrow-left"
            >
              Retour
            </AppButton>
            <KkpPlanningPageActions
            actions={[
              {
                key: 'push-dolibarr',
                label: pushingDolibarr ? 'Envoi Dolibarr…' : 'Envoyer vers Dolibarr',
                icon: 'mdi:cloud-upload-outline',
                onClick: handlePushDolibarr,
                disabled: !dossierId || toolsBusy || !meta?.can_push_dolibarr,
                loading: pushingDolibarr,
              },
              {
                key: 'preview-bdc',
                label: 'Aperçu bon de commande',
                icon: 'mdi:file-document-outline',
                onClick: openBdcPreview,
                disabled: !dossierId || toolsBusy,
              },
              {
                key: 'reset',
                label: resetting ? 'Réinitialisation…' : 'Réinitialiser l’offre',
                icon: 'mdi:refresh',
                onClick: handleReset,
                disabled: !dossierId || resetting || toolsBusy,
                loading: resetting,
                variant: 'outline-danger',
              },
            ]}
            menuItems={[
              {
                key: 'generate-bdc',
                label: generatingBdc ? 'Préparation du devis…' : 'Télécharger le devis',
                icon: 'mdi:file-pdf-box',
                onClick: handleGenerateBdc,
                disabled: !dossierId || toolsBusy,
              },
            ]}
          >
            <span className={`badge ${saving ? 'bg-light text-secondary' : 'bg-success-subtle text-success'}`}>
              {saving ? 'Enregistrement…' : 'À jour'}
            </span>
          </KkpPlanningPageActions>
          </div>
        )}
      />

      {displayError && (
        <AppAlert variant="danger">{displayError}</AppAlert>
      )}

      <PlanDeComClientInfoModal
        show={clientInfoModalOpen}
        dossier={data?.dossier}
        meta={meta}
        title={
          clientInfoPurpose === 'preview'
            ? 'Aperçu du document'
            : clientInfoPurpose === 'dolibarr'
              ? 'Envoyer vers Dolibarr'
              : 'Générer le document'
        }
        confirmLabel={
          clientInfoPurpose === 'preview'
            ? 'Voir l’aperçu PDF'
            : clientInfoPurpose === 'dolibarr'
              ? 'Créer la propal'
              : 'Télécharger'
        }
        confirmIcon={clientInfoPurpose === 'dolibarr' ? 'mdi:cloud-upload-outline' : 'mdi:file-pdf-box'}
        submitting={generatingBdc || previewPdfLoading || pushingDolibarr}
        onClose={() => !(generatingBdc || previewPdfLoading || pushingDolibarr) && setClientInfoModalOpen(false)}
        onConfirm={runBdcFromClientModal}
      />

      <PlanDeComPreviewModal
        show={previewKind === 'bdc'}
        title="Aperçu bon de commande"
        subtitle="Aperçu exact du PDF téléchargeable"
        onClose={closePreview}
        primaryAction={{
          label: generatingBdc ? 'Téléchargement…' : 'Télécharger le PDF',
          icon: 'mdi:file-pdf-box',
          disabled: !dossierId || generatingBdc || previewPdfLoading || !previewPdfUrl,
          onClick: downloadPreviewPdf,
        }}
      >
        <PlanDeComPdfPreview
          url={previewPdfUrl}
          loading={previewPdfLoading}
          error={previewPdfError}
          emptyMessage="Complétez l’offre et le nombre de salariés"
        />
      </PlanDeComPreviewModal>

      {loading && !data && <KkpPlanningEmptyState loading />}

      {data && (
        <>
          <PlanDeComDossierSwitcher
            dossiers={dossierOptions}
            current={data.dossier}
            currentId={dossierId}
            disabled={saving || resetting || toolsBusy}
            saving={saving}
            canSeeTeamDossiers={authService.isAdminCommercial() || authService.isSuperAdmin()}
            commerciaux={meta?.commerciaux ?? []}
            onRefreshDossiers={loadDossiers}
            onOpen={handleOpenDossier}
            onNew={handleNewDossier}
            onDuplicate={handleDuplicateDossier}
            onDelete={handleDeleteDossier}
            onRename={handleRenameDossier}
          />
          <AppFilterBar showReset={false}>
            <AppFilterField col="col-12 col-md-6 col-lg-3" label="Titre du chiffrage">
              <AppInput
                size="sm"
                type="text"
                value={data.dossier.titre ?? ''}
                onChange={(e) => updateDossierField('titre', e.target.value)}
                onBlur={(e) => updateDossierField('titre', e.target.value.trim() || null)}
                placeholder="Ex. Adhésion ACME"
                autoComplete="off"
              />
            </AppFilterField>
            <AppFilterField
              col="col-12 col-md-6 col-lg-3"
              className={!data.dossier.entreprise_label ? 'kkp-pdc-field--missing' : ''}
              label={(
                <>
                  Entreprise
                  <span className="kkp-pdc-required" aria-hidden="true"> *</span>
                </>
              )}
            >
              <KkpEntrepriseAutocomplete
                value={data.dossier.entreprise_label ?? ''}
                onSelect={handleEntrepriseSelect}
                onResolvingChange={handleEntrepriseResolving}
                placeholder="Rechercher dans Dolibarr…"
                size="sm"
              />
            </AppFilterField>
            <AppFilterField col="col-12 col-md-6 col-lg-2" label="Nom du client">
              <AppInput
                size="sm"
                type="text"
                value={data.dossier.client_label ?? ''}
                onChange={(e) => updateDossierField('client_label', e.target.value)}
                onBlur={(e) => updateDossierField('client_label', e.target.value.trim() || null)}
                placeholder={
                  resolvingClient
                    ? 'Recherche contact Dolibarr…'
                    : 'Contact rattaché ou saisie libre…'
                }
                disabled={resolvingClient}
                autoComplete="off"
              />
            </AppFilterField>
            <AppFilterCommercialSelect
              id="klub-chiffrage-commercial"
              col="col-6 col-md-4 col-lg-2"
              className={data.dossier.commercial_id == null ? 'kkp-pdc-field--missing' : ''}
              label={(
                <>
                  Commercial
                  <span className="kkp-pdc-required" aria-hidden="true"> *</span>
                </>
              )}
              value={data.dossier.commercial_id != null ? [String(data.dossier.commercial_id)] : []}
              onChange={(next) => updateDossierField(
                'commercial_id',
                next[0] ? Number(next[0]) : null,
              )}
              options={commercialOptions}
              emptyLabel="Aucun commercial trouvé"
            />
          </AppFilterBar>

          <div className="card kkp-planning-results-card mb-3">
            <div className="card-body">
              <h2 className="h6 mb-3">Offre Klub</h2>
              <div className="row g-3 align-items-end">
                <div className={`col-md-6 col-lg-5 ${!data.dossier.type_abonnement_klub ? 'kkp-pdc-field--missing' : ''}`}>
                  <label className="form-label" htmlFor="klub-offre">Offre</label>
                  <AppSelect
                    id="klub-offre"
                    value={data.dossier.type_abonnement_klub ?? ''}
                    onChange={(e) => updateDossierField('type_abonnement_klub', e.target.value || null)}
                  >
                    <option value="">Choisir une offre…</option>
                    {klubOffers.map((offer) => (
                      <option key={offer.id ?? offer.nom} value={offer.nom}>
                        {offer.nom}
                      </option>
                    ))}
                  </AppSelect>
                </div>
                <div className={`col-md-4 col-lg-3 ${!(Number(data.dossier.nombre_salaries_klub) > 0) ? 'kkp-pdc-field--missing' : ''}`}>
                  <label className="form-label" htmlFor="klub-salaries">Nombre de salariés</label>
                  <AppInput
                    id="klub-salaries"
                    type="number"
                    min="1"
                    value={data.dossier.nombre_salaries_klub ?? ''}
                    onChange={(e) => updateDossierField(
                      'nombre_salaries_klub',
                      e.target.value === '' ? null : Number(e.target.value),
                    )}
                    placeholder="Effectif"
                  />
                </div>
              </div>
              {selectedKlubOffer && (
                <p className="small text-secondary-light mt-3 mb-0">
                  Tranche : {formatPlanDeComMoney(tarifForEmployees(selectedKlubOffer, data.dossier.nombre_salaries_klub))}
                  {selectedAboProductRef ? ` (${selectedAboProductRef})` : ''}
                  {' · '}
                  Frais d’init. : {formatPlanDeComMoney(selectedKlubOffer.frais_initial_ht)}
                  {selectedKlubOffer.product_ref_frais
                    ? ` (${selectedKlubOffer.product_ref_frais})`
                    : ''}
                  {inclusions.length > 0 ? ` · Inclus : ${inclusions.join(', ')}` : ''}
                </p>
              )}
            </div>
          </div>

          {recap && (
            <div className="card kkp-pdc-recap-card">
              <div className="card-body">
                <div className="kkp-pdc-recap-header">
                  <h2 className="kkp-pdc-recap-title">Récapitulatif</h2>
                  <p className="kkp-pdc-recap-subtitle">
                    {recap.tranche || 'Choisissez une offre et le nombre de salariés'}
                  </p>
                </div>
                <div className="kkp-pdc-recap-hero">
                  <div className="kkp-pdc-recap-hero__item">
                    <div className="kkp-pdc-recap-label">Total HT</div>
                    <div className="kkp-pdc-recap-value kkp-pdc-recap-value--primary">
                      {formatPlanDeComMoney(recap.budget_global_net_ht)}
                    </div>
                  </div>
                  <div className="kkp-pdc-recap-hero__item">
                    <div className="kkp-pdc-recap-label">Total TTC</div>
                    <div className="kkp-pdc-recap-value kkp-pdc-recap-value--primary">
                      {formatPlanDeComMoney(recap.budget_global_net_ttc)}
                    </div>
                  </div>
                </div>
                <div className="kkp-pdc-recap-details">
                  <div className="kkp-pdc-recap-detail">
                    <div className="kkp-pdc-recap-label">Abonnement annuel</div>
                    <div className="kkp-pdc-recap-value">{formatPlanDeComMoney(recap.montant_abo_annuel)}</div>
                  </div>
                  <div className="kkp-pdc-recap-detail">
                    <div className="kkp-pdc-recap-label">Frais d’initialisation</div>
                    <div className="kkp-pdc-recap-value">{formatPlanDeComMoney(recap.frais_techniques)}</div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
      {modal}
    </MasterLayout>
  );
};

export default KlubChiffragePage;
